import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import colors from 'components/utils/colors'

import ToggleButtonGroup from 'components/Shared/ToggleButtonGroup/Index'
import TimePartSelect from './TimePartSelect/Index'

const StyledWrapper = styled.div`
  display: flex;
  align-items: center
`

const TimeLabel = styled.div`
  display: flex;
  font-family: Exo;
  font-size: 14px;
  font-weight: 500;
  margin-left: 6px;
  margin-right: 16px;
  color: ${colors.contentSecondaryActive};
`

const TimeInput = styled.div`
  display: flex;
  border: 1px solid ${colors.borderSecondaryDefault};
  border-radius: 3px;
  border-radius: 3px;
  background: #FFFFFF;
  padding: 3px 14px;
  align-items: center;
}
`
const TimeSeparator = styled.span`
  padding: 0 4px;
  font-size: 14px;
`

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  padding-left: 8px;
  max-width: 64px;
  button {
    padding: 0;
    max-width: 32px;
  }
`

const getTimeOptions = ({ maxNumber }) => {
  const result = []
  for (let i = 0; i < maxNumber; i++) {
    const val = String(i).padStart(2, '0')
    result.push({ label: val, value: i })
  }
  return result
}

const get12HoursOptions = () => {
  const result = []
  for (let i = 1; i <= 12; i++) {
    const val = String(i)
    result.push({ label: val, value: i })
  }
  return result
}

const getDateParts = (date, timeFormat24) => {
  const hours24 = date.getTime() ? date.getHours() : date.getUTCHours()
  const hours_ = timeFormat24 ? hours24 : (hours24 % 12 || 12)
  const minutes_ = date.getMinutes()
  const seconds_ = date.getSeconds()
  const ampm_ = hours24 >= 12 ? 'pm' : 'am'
  return [hours24, hours_, minutes_, seconds_, ampm_]
}

const TimePicker = ({ date = new Date(0), value, onChange, timeFormat24 }) => {
  const [hours24, hours_, minutes_, seconds_, ampm_] = getDateParts(date, timeFormat24)

  const [hours, setHours] = useState(hours_)
  const [minutes, setMinutes] = useState(minutes_)
  const [seconds, setSeconds] = useState(seconds_)
  const [ampm, setAmpm] = useState(ampm_)

  const { t } = useTranslation()

  useEffect(() => {
    const [, hours_, minutes_, seconds_, ampm_] = getDateParts(date, timeFormat24)
    setHours(hours_)
    setMinutes(minutes_)
    setSeconds(seconds_)
    setAmpm(ampm_)
  }, [hours_, minutes_, seconds_, ampm_]) // eslint-disable-line react-hooks/exhaustive-deps

  const onHourChange = (h) => {
    setHours(h)
    let newHours = h
    if (!timeFormat24) {
      if (ampm === 'pm' && h < 12) {
        newHours = h + 12
      }
      if (ampm === 'am' && h === 12) {
        newHours = 0
      }
    }

    onChange(`${newHours}:${minutes}`)
  }

  const onMinChange = (val) => {
    setMinutes(val)
    onChange(`${hours24}:${val}`)
  }

  const onSecChange = (val) => {
    setSeconds(val)
    // we have to set seconds directly in Date bacause the library
    // doesn't support seconds in custom time field
    date.setSeconds(val)
    onChange(`${hours24}:${minutes}`)
  }

  const onAmPmToggle = (val) => {
    setAmpm(val)
    const newHours = (hours24 + 12) % 24
    onChange(`${newHours}:${minutes}`)
  }

  const hourOptions = useMemo(() => {
    return timeFormat24 ? getTimeOptions({ maxNumber: 24 }) : get12HoursOptions()
  }, [timeFormat24])
  const minOptions = useMemo(() => getTimeOptions({ maxNumber: 60 }), [])
  const secOptions = useMemo(() => getTimeOptions({ maxNumber: 60 }), [])

  const buttons = [
    {
      value: 'am',
      label: t('datetimeInput.am'),
    },
    {
      value: 'pm',
      label: t('datetimeInput.pm'),
    },
  ]

  return (
    <StyledWrapper>
      <TimeLabel>{t('datetimeInput.timeLabel')}</TimeLabel>
      <TimeInput>
        <TimePartSelect
          value={hours}
          options={hourOptions}
          optionsTitle={t('datetimeInput.hours')}
          onChange={onHourChange}
        />
        <TimeSeparator>:</TimeSeparator>
        <TimePartSelect
          value={minutes}
          options={minOptions}
          optionsTitle={t('datetimeInput.minutes')}
          onChange={onMinChange}
        />
        <TimeSeparator>:</TimeSeparator>
        <TimePartSelect
          value={seconds}
          options={secOptions}
          optionsTitle={t('datetimeInput.seconds')}
          onChange={onSecChange}
        />
      </TimeInput>

      {!timeFormat24 && (
        <StyledToggleButtonGroup
          value={ampm}
          onChange={onAmPmToggle}
          buttons={buttons}
          useFullWidth
        />
      )}
    </StyledWrapper>
  )
}

TimePicker.propTypes = {
  date: PropTypes.instanceOf(Date),
  value: PropTypes.string,
  onChange: PropTypes.func,
  timeFormat24: PropTypes.bool,
}

export default TimePicker
